import { useCallback, useMemo, useState } from "react";
import { useTheme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core";
import { useAtomicStyles } from "styles";
import { useDispatch, useSelector } from "react-redux";
import { useCurrentMerchantId, useTypedTranslations } from "../../hooks";
import { actions, selectors } from "../../redux-store";
import moment from "moment";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      display: "inline",
      marginBottom: 20,
    },
    searchBar: {
      marginBottom: 20,
      padding: 0,
    },
    inputProps: {
      width: "100%",
    },
    dropDown: {
      width: "65%",
      maxHeight: 300,
      [theme.breakpoints.down("xs")]: {
        maxHeight: 150,
        width: "81%",
      },
    },
    scanButton: {},
    paper: {
      margin: 15,
    },
    scanner: {
      marginRight: 15,
      marginLeft: 15,
    },
    consumeButton: {
      width: "100%",
      marginTop: 15,
      marginBottom: 15,
      display: "flex",
      justifyContent: "center",
    },
  }),
);

const constraints: MediaTrackConstraints = {
  facingMode: { ideal: "environment" },
};

const useQrCodePage = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const classes = useMemo(() => ({}), []);
  const atomicStyles = useAtomicStyles();
  const [t] = useTypedTranslations();
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventTitle, setSelectedEventTitle] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [qrUuid, setQrUuid] = useState(null);
  const isCheckingQrCode = useSelector(
    selectors.getAjaxIsLoadingByApi(
      actions.getMerchantsSingleUseEventsByMerchantId.api,
    ),
  );
  const isConsumingQrCode = useSelector(
    selectors.getAjaxIsLoadingByApi(
      actions.patchSingleUseEventsBySingleUseEventId.api,
    ),
  );

  const dispatch = useDispatch();

  const merchantId = useCurrentMerchantId();

  const events = useSelector(selectors.getEventsList).filter((ev) => {
    return moment(ev.expire).isAfter(moment()) && ev.isSingleUse;
  });

  const singleUseEvent = useSelector(selectors.getSingleUseEvent);

  const eventData = useMemo(() => {
    return events.filter((ev) => {
      return ev.title.toLocaleLowerCase();
    });
  }, [events]);

  const options: { label: string; value: string | number }[] = useMemo(() => {
    return eventData.map((ed) => ({
      label: ed.title,
      value: ed._id,
    }));
  }, [eventData]);

  const handleSelection = useCallback(
    (eventId: string) => {
      setSelectedEventId(eventId);
      const event = events.find((e) => e._id === eventId);
      setSelectedEventTitle(event.title);
    },
    [events],
  );

  const openScanner = useCallback(() => {
    setScannerOpen(true);
  }, []);

  const onScannedData = useCallback(
    (res) => {
      if (res?.text) {
        setQrUuid(res.text);
        setScannerOpen(false);
        const payload = {
          merchantId,
          eventId: selectedEventId,
          qrUuid: res.text,
        };
        dispatch(
          actions.getMerchantsSingleUseEventsByMerchantId.request(payload),
        );
      }
    },
    [dispatch, merchantId, selectedEventId],
  );

  const onConsumeSingleUseEvent = useCallback(() => {
    if (singleUseEvent === null) {
      return;
    }
    dispatch(
      actions.patchSingleUseEventsBySingleUseEventId.request({
        singleUseEventId: singleUseEvent._id,
        hasBeenUsed: true,
      }),
    );
  }, [dispatch, singleUseEvent]);

  const onRemoveQrUuid = useCallback(() => {
    setQrUuid(null);
    dispatch(actions.resetSingleUseEvent());
  }, [dispatch]);

  return {
    styles,
    classes,
    atomicStyles,
    t,
    options,
    handleSelection,
    selectedEventId,
    selectedEventTitle,
    scannerOpen,
    openScanner,
    qrUuid,
    onScannedData,
    constraints,
    singleUseEvent,
    onConsumeSingleUseEvent,
    onRemoveQrUuid,
    isCheckingQrCode,
    isConsumingQrCode,
  };
};

export { useQrCodePage as default };
